<script setup lang="ts">
import * as yup from 'yup';
import { type Note, type NoteForm, notesApi } from '@/api/notes';
import { useMutation, useQueryClient, useQuery } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import { useForm } from 'vee-validate';
import { computed } from 'vue';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';
import { useShowVoided } from '@/composables/useShowVoided';
import { authorIdentityErrorText } from '@/utils/author-identity';
import { clinicalAuthorName } from '@/utils/clinical-author';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';

interface Props {
  noteableType: string;
  noteableId: number;
  canCreate?: boolean;
}

const props = withDefaults(defineProps<Props>(), { canCreate: true });

// Notes about a patient or a shift are registros clínicos, with a history;
// notes about a caregiver are not.
const clinical = computed(() => ['patient', 'shift'].includes(props.noteableType.toLowerCase()));

const validationSchema = yup.object({
  text: yup.string().required('El comentario es requerido'),
});

const initialValues = {
  text: '',
};

const { handleSubmit, values, errors, defineField, resetForm } = useForm<NoteForm>({ validationSchema, initialValues });

const [text, textAttrs] = defineField('text');

const queryClient = useQueryClient();

const { data: notes, isLoading } = useQuery({
  queryKey: ['notes', props.noteableType, props.noteableId],
  queryFn: () => notesApi.index(props.noteableType, props.noteableId),
});

// A clinical note is anulado, never deleted: anulados show on demand, crossed out.
const { showVoided, voidedCount, visibleItems: visibleNotes } = useShowVoided(notes);

function noteLabel(note: Note) {
  return `Comentario de ${clinicalAuthorName(note.author) || note.userEmail} · ${note.createdAgo}`;
}

const { mutate: createNote, isError: isCreateError, error: createError, isPending } = useMutation<Note, AxiosError>(
  {
    mutationFn: () => notesApi.create({
      text: values.text,
      noteableType: props.noteableType,
      noteableId: props.noteableId,
    }),
    onSuccess: () => {
      resetForm();
      queryClient.invalidateQueries({
        queryKey: ['notes', props.noteableType, props.noteableId],
      });
    },
  },
);

const onSubmit = handleSubmit(() => {
  createNote();
});

function errorMessage(error: AxiosError) {
  // The user closed the name and RUT dialog: the server says what is missing.
  return authorIdentityErrorText(error) ||
    (error.response?.data as { message?: string })?.message || 'Error al crear el comentario';
}

</script>
<template>
  <v-form
    v-if="props.canCreate"
    @submit="onSubmit"
  >
    <v-textarea
      v-model="text"
      variant="outlined"
      label="Comentario"
      v-bind="textAttrs"
      :error-messages="errors.text"
      rows="4"
      auto-grow
      placeholder="Escribe tu comentario aquí..."
    />
    <div class="d-flex justify-end">
      <v-btn
        type="submit"
        color="primary"
        :disabled="isPending"
        :loading="isPending"
      >
        {{ isPending ? 'Guardando...' : 'Guardar' }}
      </v-btn>
    </div>
    <v-alert
      v-if="isCreateError && createError"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ errorMessage(createError) }}
    </v-alert>
  </v-form>
  <div class="mt-6">
    <div
      v-if="clinical && voidedCount > 0"
      class="d-flex justify-end mb-2"
    >
      <show-voided-switch
        v-model="showVoided"
        :count="voidedCount"
      />
    </div>
    <div
      v-if="isLoading"
      class="text-center py-8"
    >
      <v-progress-circular indeterminate />
      <div class="text-body-2 text-medium-emphasis mt-2">
        Cargando comentarios...
      </div>
    </div>
    <div
      v-else-if="visibleNotes.length === 0"
      class="text-center py-8"
    >
      <v-icon
        size="large"
        color="grey-lighten-1"
        class="mb-2"
      >
        mdi-comment-outline
      </v-icon>
      <div class="text-body-2 text-medium-emphasis">
        No hay comentarios
      </div>
    </div>
    <div
      v-else
      class="notes-list"
    >
      <transition-group
        name="fade"
        tag="div"
      >
        <div
          v-for="(note, index) in visibleNotes"
          :key="note.id"
          class="note-item"
        >
          <div class="d-flex flex-wrap ga-1 align-center mb-1">
            <clinical-author-line
              v-if="note.author"
              :author="note.author"
              :at="note.humanCreatedAt"
            />
            <span
              v-else
              class="text-caption font-weight-bold"
            >
              <v-icon
                size="small"
                color="primary"
              >mdi-account</v-icon>
              {{ note.userEmail }}
            </span>
            <span class="text-caption text-medium-emphasis">
              <v-icon
                size="small"
              >mdi-clock-outline</v-icon>
              {{ note.createdAgo }}
            </span>
            <clinical-record-history
              v-if="clinical"
              item-type="Note"
              :item-id="note.id"
              :edited="note.edited"
              :label="noteLabel(note)"
            />
            <clinical-record-void
              v-if="clinical"
              item-type="Note"
              :item-id="note.id"
              :can-void="note.canVoid"
              :label="noteLabel(note)"
              correction="text"
              :current-text="note.text"
            />
          </div>
          <clinical-void-mark
            :voided="note.voided"
            :details="note.voidDetails"
            class="mb-1"
          />
          <div
            class="text-body-2 mb-4"
            :class="{ 'text-decoration-line-through text-medium-emphasis': note.voided }"
          >
            {{ note.text }}
          </div>
          <v-divider
            v-if="index < visibleNotes.length - 1"
            class="my-2"
          />
        </div>
      </transition-group>
    </div>
  </div>
</template>

<style scoped>

.note-item:last-child {
  padding-bottom: 0;
}

.fade-enter-active {
  transition: all .3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
</style>

